interface DashboardCacheEntry<T> {
  data: T;
  expiresAt: number;
}

const dashboardCache = new Map<string, DashboardCacheEntry<unknown>>();
const dashboardRequests = new Map<string, Promise<unknown>>();
const dashboardCacheVersions = new Map<string, number>();
const DEFAULT_DASHBOARD_CACHE_TTL = 60_000;
let dashboardCacheEpoch = 0;

export function getDashboardCache<T>(key: string): T | null {
  const entry = dashboardCache.get(key);
  if (!entry) return null;

  if (Date.now() > entry.expiresAt) {
    dashboardCache.delete(key);
    return null;
  }

  return entry.data as T;
}

export function setDashboardCache<T>(
  key: string,
  data: T,
  ttl = DEFAULT_DASHBOARD_CACHE_TTL,
): void {
  dashboardCache.set(key, {
    data,
    expiresAt: Date.now() + ttl,
  });
}

export function getDashboardCacheVersion(key: string): string {
  return `${dashboardCacheEpoch}:${dashboardCacheVersions.get(key) || 0}`;
}

export function getOrCreateDashboardRequest<T>(
  key: string,
  createRequest: () => Promise<T>,
): Promise<T> {
  const pending = dashboardRequests.get(key);
  if (pending) return pending as Promise<T>;

  const request = createRequest();
  dashboardRequests.set(key, request);
  request.then(
    () => {
      if (dashboardRequests.get(key) === request) dashboardRequests.delete(key);
    },
    () => {
      if (dashboardRequests.get(key) === request) dashboardRequests.delete(key);
    },
  );
  return request;
}

export function invalidateDashboardCache(key?: string): void {
  if (key) {
    dashboardCache.delete(key);
    dashboardRequests.delete(key);
    dashboardCacheVersions.set(key, (dashboardCacheVersions.get(key) || 0) + 1);
    return;
  }

  dashboardCache.clear();
  dashboardRequests.clear();
  dashboardCacheVersions.clear();
  dashboardCacheEpoch += 1;
}
